import { Table } from 'antd';
import { FC, useMemo, useState } from 'react';
import Collapse from '@dynamika-shared/ui/Collapse/Collapse';
import { CollapseHeader, CollapseState } from '../components/CollapseHeader';
import {
  DXAReading,
  DXAWorkstationDataDTO,
} from '@dynamika-shared/types/DXAReading';
import { makeColumns } from './Table.columns';
import { twJoin } from '@dynamika-shared/utils/twMerge';
import { Modal } from '@dynamika-shared/ui/Modal/Modal';
import { NoContent } from '@dynamika-shared/ui/NoContent/NoContent';

export type ReadingsTableProps = {
  onRemove: (reading: DXAReading) => void;
  visitData: DXAWorkstationDataDTO | null;
  loading: boolean;
  readOnly: boolean;
};

const collapseStates: CollapseState[] = ['preview', 'open', 'closed'];

const getNextState =
  (states = collapseStates) =>
  (state: CollapseState): CollapseState => {
    const index = states.findIndex(s => s === state);
    return index + 1 >= states.length ? states[0] : states[index + 1];
  };

export const ReadingsTable: FC<ReadingsTableProps> = ({
  onRemove,
  visitData,
  loading,
  readOnly,
}) => {
  const readings = visitData?.dxaScores || [];
  const [collapseValue, setCollapseValue] = useState<CollapseState>('closed');

  const [valueToRemove, setValueToRemove] = useState<DXAReading | null>(null);

  const selectToRemove = (reading: DXAReading) => {
    setValueToRemove(reading);
  };

  const handleRemove = () => {
    onRemove(valueToRemove as DXAReading);
    setValueToRemove(null);
  };

  const columns = makeColumns({ onRemove: selectToRemove, readOnly });

  useMemo(() => {
    if (readings.length) {
      setCollapseValue('preview');
    }
  }, [visitData?.dxaScores]);

  const sortedReadings = useMemo(() => {
    return readings?.sort((a, b) => a?.labelName?.localeCompare(b?.labelName || '') || 0);
  }, [readings]) || [];

  return (
    <div>
      <Collapse
        open={collapseValue !== 'closed'}
        hideChevron
        notUseInnerState
        resize={readings.length || collapseValue}
        onClick={() =>
          setCollapseValue(
            getNextState(
              readings.length < 5
                ? collapseStates.filter(s => s !== 'open')
                : collapseStates
            )
          )
        }
        label={
          <CollapseHeader
            collapseValue={collapseValue}
            title={'Selected for Reading'}
          />
        }
      >
        <div
          id='DXA_Added_Readings'
          className={twJoin(
            'overflow-auto',
            collapseValue === 'preview' && 'max-h-[36.5rem]',
            collapseValue === 'open' && 'h-[calc(100vh-250px)]'
          )}
        >
          <Table
            locale={{
              emptyText: (
                <NoContent
                  iconName={'noContent'}
                  title={
                    <span className='text-spanishGray'>
                      No values were selected for{' '}
                      <b className='text-black'>Reading</b> yet.
                      <br />
                      Retrieve results from workstation, if not done yet and
                      continue selecting the values.
                    </span>
                  }
                >
                  {' '}
                </NoContent>
              ),
            }}
            loading={loading}
            pagination={false}
            columns={columns}
            dataSource={sortedReadings}
          />
        </div>
      </Collapse>
      <Modal
        width={440}
        title={<div className='w-full flex'>Remove</div>}
        onOk={handleRemove}
        onCancel={() => setValueToRemove(null)}
        okText='Remove'
        centered
        okClassName='ant-btn-dangerous'
        open={!!valueToRemove}
      >
        Are you sure you want to remove this Reading from selected list?
      </Modal>
    </div>
  );
};
